<script setup lang="ts">
import { Gesuch } from '@/models/Gesuch'
import LiegenschaftCard from '@/components/liegenschaft/gesuch-card.vue'
import LiegenschaftSelectDialog from '@/components/liegenschaft/dialog/select.vue'
import GesuchLiegenschaftForm from '@/components/liegenschaft/gesuch-liegenschaft-form.vue'
import ConfirmDlg from '@/components/confirm-dlg.vue'
import { computed, onMounted, ref, watch, type Ref } from 'vue'
import { useRoute } from 'vue-router'
import { Liegenschaft } from '@/models/Liegenschaft'
import { useToast } from '@/composables/toast'
import { useI18n } from 'vue-i18n'
import { createModel } from '@kp/js-model'
import { LiegenschaftData } from '@/models/apollon_data/LiegenschaftData'
import type { Data } from '@/models/apollon_data/Data'
import type { StepType } from './GesuchPanel.vue'

const {
    gesuch,
    loading = false,
    readOnly = true,
} = defineProps<{
    gesuch: Gesuch
    loading?: boolean
    readOnly?: boolean
}>()
const showLiegenschaftDialog = ref(false)
const confirmDelDlg = ref(false)
const editing = ref(false)
const toDeleteId: Ref<number | null> = ref(null)
const editLiegenschaft: Ref<LiegenschaftData | null> = ref(null)
const liegenschaften: Ref<LiegenschaftData[]> = ref([])

const route = useRoute()
const { t } = useI18n()

const liegenschaftenConfig = computed(() => {
    return gesuch?.config?.config?.objekte || null
})
const edit_id = computed(() => {
    // is set from a route if a certain liegenschaft need to be edited when the route opens:
    return route.params.edit_id || null
})

const emit = defineEmits<{
    (e: 'editingmode', value: boolean): void
    (e: 'gesuchdatachanged', data: Data | null): void
    (e: 'next', nextStep: StepType | null): void
}>()

watch(
    () => gesuch,
    (g) => {
        liegenschaften.value = g?.data?.liegenschaften || []
    },
    { deep: true },
)
watch(
    () => editing.value,
    (e) => {
        emit('editingmode', e)
    },
)

onMounted(() => {
    // If we get an edit_id from the route params (see computed props above),
    // then we open the Liegenschaft for editing:
    if (edit_id.value) {
        const liegenschaft = liegenschaften.value.find((l) => l.liegenschaft?.id === edit_id.value)
        if (liegenschaft) {
            editLiegenschaftZusatzdaten(liegenschaft)
        }
    }
    if (gesuch) {
        liegenschaften.value = gesuch.data?.liegenschaften || []
    }
})
function assignLiegenschaft(liegenschaft: Liegenschaft | null) {
    if (!liegenschaften.value) {
        gesuch.data?.set('liegenschaften', [])
    }
    if (liegenschaften.value.some((e) => e.liegenschaft?.id === liegenschaft?.id)) {
        useToast().add({ text: t('duplicat_object_error'), color: 'info' })
        return
    }
    const lisi = gesuch.data?.liegenschaften?.map((e) => e) || ([] as LiegenschaftData[])
    const liegenschaftEntry = createModel(LiegenschaftData, {
        liegenschaft: liegenschaft,
        liegenschaft_zusatzdaten: null,
    })
    lisi.push(liegenschaftEntry)
    gesuch.data?.set('liegenschaften', lisi)
    liegenschaften.value = gesuch.data?.liegenschaften || []
    emit('gesuchdatachanged', gesuch.data)
    editLiegenschaftZusatzdaten(liegenschaftEntry)
    closeLiegenschaftSelect()
}
function openLiegenschaftSelect() {
    showLiegenschaftDialog.value = true
}
function closeLiegenschaftSelect() {
    showLiegenschaftDialog.value = false
}
function editLiegenschaftZusatzdaten(liegenschaft: LiegenschaftData) {
    if (liegenschaft && liegenschaft.liegenschaft instanceof Liegenschaft) {
        editLiegenschaft.value = liegenschaft
    } else {
        editLiegenschaft.value = createModel(LiegenschaftData, {
            liegenschaft: createModel(Liegenschaft, liegenschaft.liegenschaft || undefined),
            liegenschaft_zusatzdaten: liegenschaft.liegenschaft_zusatzdaten,
        })
    }
    editing.value = true
}
function onNewLiegenschaft() {
    showLiegenschaftDialog.value = false
    editing.value = true
    editLiegenschaft.value = createModel(LiegenschaftData, {
        liegenschaft: new Liegenschaft(),
        liegenschaft_zusatzdaten: {},
    })
}
async function confirmDestroy() {
    if (!liegenschaften.value) {
        gesuch.data?.set('liegenschaften', [])
    }
    const index = liegenschaften.value.findIndex(
        (data) => data.liegenschaft?.id === toDeleteId.value,
    )
    const lisi = gesuch.data?.liegenschaften || []
    lisi.splice(index, 1)
    gesuch.data?.set('liegenschaften', lisi)
    toDeleteId.value = null
    confirmDelDlg.value = false
    emit('gesuchdatachanged', gesuch.data)
}
function deleteLiegenschaft(liegenschaftId: number) {
    toDeleteId.value = liegenschaftId
    confirmDelDlg.value = true
}
function onCloseLiegenschaftForm() {
    editing.value = false
    emit('gesuchdatachanged', gesuch.data)
}
</script>

<template>
    <v-container fluid class="kp-large-container">
        <v-row v-if="editing">
            <v-col>
                <GesuchLiegenschaftForm
                    :gesuch="gesuch"
                    :liegenschaft="editLiegenschaft"
                    :read-only="readOnly"
                    @close="onCloseLiegenschaftForm"
                ></GesuchLiegenschaftForm>
            </v-col>
        </v-row>
        <template v-else>
            <v-alert
                v-if="liegenschaftenConfig?.infotext && !readOnly"
                type="info"
                :icon="false"
                border="start"
                colored-border
                variant="outlined"
                elevation="2"
            >
                <template #text>
                    <div class="banner-container">
                        <span v-html="liegenschaftenConfig?.infotext.replace('\n', '<br />')">
                        </span>
                        <v-btn
                            color="action"
                            variant="flat"
                            :disabled="loading"
                            @click="openLiegenschaftSelect"
                            prepend-icon="fas fa-plus"
                        >
                            {{ $t('assign_object') }}
                        </v-btn>
                    </div>
                </template>
            </v-alert>
            <div v-else>
                <v-spacer />
                <v-btn
                    v-if="!readOnly"
                    color="accent"
                    :disabled="loading"
                    @click="openLiegenschaftSelect"
                >
                    <v-icon left>fas fa-plus</v-icon>
                    {{ $t('assign_object') }}
                </v-btn>
            </div>
            <v-card variant="flat">
                <v-card-text>
                    <v-row>
                        <v-col
                            v-for="liegenschaft in liegenschaften"
                            :key="liegenschaft?.liegenschaft?.id || 'new'"
                            cols="12"
                            sm="12"
                            md="6"
                            lg="4"
                        >
                            <LiegenschaftCard
                                :gesuch="gesuch"
                                :liegenschaft="liegenschaft"
                                :read-only="readOnly"
                                @edit-liegenschaft-zusatzdaten="editLiegenschaftZusatzdaten"
                                @delete-liegenschaft="deleteLiegenschaft"
                            ></LiegenschaftCard>
                        </v-col>
                    </v-row>
                </v-card-text>
                <v-card-actions>
                    <v-spacer />
                    <v-btn
                        color="action"
                        :loading="loading"
                        @click="$emit('next', null)"
                        variant="flat"
                        append-icon="fas fa-arrow-right"
                    >
                        {{ $t('next_step') }}
                    </v-btn>
                </v-card-actions>
            </v-card>
        </template>

        <LiegenschaftSelectDialog
            v-if="showLiegenschaftDialog"
            v-model:model-value="showLiegenschaftDialog"
            :loading="loading"
            :gesuch="gesuch"
            @close="closeLiegenschaftSelect"
            @assign="assignLiegenschaft"
            @new-liegenschaft="onNewLiegenschaft"
        ></LiegenschaftSelectDialog>
        <ConfirmDlg
            v-model:model-value="confirmDelDlg"
            :title="$t('bestaetigung')"
            :text="$t('really_remove_obj')"
            @yes="confirmDestroy"
            @no="
                () => {
                    toDeleteId = null
                    confirmDelDlg = false
                }
            "
        />
    </v-container>
</template>

<style lang="css" scoped>
.banner-container {
    display: flex;
    justify-content: space-between;
    align-items: center;
}
</style>
